import apiClient from '../client';

// Types
export interface ApiStaff {
  id: number;
  storeId: number;
  storeName?: string;
  userId: string;
  name: string;
  role: 'admin' | 'user';
  isActive: boolean;
  lastLogin?: string;
  createdAt?: string;
  updatedAt?: string;
  accountStatus: 'active' | 'inactive' | 'locked';
}

export interface ApiStaffListParams {
  search?: string;
  storeId?: number;
  status?: 'active' | 'inactive' | 'locked';
  role?: 'admin' | 'user';
  sortBy?: 'name' | 'store' | 'role' | 'created_at' | 'last_login';
  order?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
}

export interface ApiStaffListResponse {
  data: ApiStaff[];
  meta: {
    totalCount: number;
    page: number;
    perPage: number;
    totalPages: number;
  };
}

export interface ApiStaffDetail extends ApiStaff {
  statistics: {
    totalLoginCount: number;
    daysSinceRegistration: number;
  };
}

export interface ApiStaffCreate {
  storeId: number;
  userId: string;
  name: string;
  password: string;
  role: 'admin' | 'user';
  isActive?: boolean;
}

export interface ApiStaffUpdate {
  name?: string;
  password?: string;
  role?: 'admin' | 'user';
  isActive?: boolean;
  storeId?: number;
}

export interface ApiLoginHistory {
  id: number;
  timestamp: string;
  ipAddress: string;
  userAgent: string;
  status: string;
  location: string;
}

export interface ApiActivityLog {
  id: number;
  timestamp: string;
  action: string;
  description: string;
  ipAddress: string;
  details: Record<string, any>;
}

export interface ApiLoginHistoryResponse {
  staffId: number;
  totalCount: number;
  data: ApiLoginHistory[];
}

export interface ApiActivityLogsResponse {
  staffId: number;
  totalCount: number;
  data: ApiActivityLog[];
}

// API endpoints
export const staffApi = {
  // Get staff list
  getStaffList: async (params?: ApiStaffListParams): Promise<ApiStaffListResponse> => {
    const urlParams = new URLSearchParams();

    if (params) {
      if (params.search) urlParams.append('search', params.search);
      if (params.storeId) urlParams.append('store_id', params.storeId.toString());
      if (params.status) urlParams.append('status', params.status);
      if (params.role) urlParams.append('role', params.role);
      if (params.sortBy) urlParams.append('sort_by', params.sortBy);
      if (params.order) urlParams.append('order', params.order);
      if (params.page) urlParams.append('page', params.page.toString());
      if (params.perPage) urlParams.append('per_page', params.perPage.toString());
    }

    const queryString = urlParams.toString();
    return apiClient.adminRequest<ApiStaffListResponse>(
      `/admin/staff${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Get staff detail
  getStaffDetail: async (staffId: number): Promise<ApiStaffDetail> => {
    return apiClient.adminRequest<ApiStaffDetail>(`/admin/staff/${staffId}`, { method: 'GET' });
  },

  // Create new staff
  createStaff: async (data: ApiStaffCreate): Promise<ApiStaff> => {
    return apiClient.adminRequest<ApiStaff>('/admin/staff', {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  // Update staff
  updateStaff: async (staffId: number, data: ApiStaffUpdate): Promise<ApiStaff> => {
    return apiClient.adminRequest<ApiStaff>(`/admin/staff/${staffId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json'
      }
    });
  },

  // Toggle active status
  toggleActive: async (staffId: number): Promise<ApiStaff> => {
    return apiClient.adminRequest<ApiStaff>(`/admin/staff/${staffId}/toggle-active`, {
      method: 'PUT'
    });
  },

  // Get login history
  getLoginHistory: async (staffId: number, limit?: number): Promise<ApiLoginHistoryResponse> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return apiClient.adminRequest<ApiLoginHistoryResponse>(
      `/admin/staff/${staffId}/login-history${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  },

  // Get activity logs
  getActivityLogs: async (staffId: number, limit?: number): Promise<ApiActivityLogsResponse> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    const queryString = params.toString();
    return apiClient.adminRequest<ApiActivityLogsResponse>(
      `/admin/staff/${staffId}/activity-logs${queryString ? `?${queryString}` : ''}`,
      { method: 'GET' }
    );
  }
};
